import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then((res) => setPlans(res.data.plans));
  }, []);

  const handleChoose = async (planId) => {
    if (!user) {
      navigate("/signup");
      return;
    }
    if (planId === "free") {
      toast.info("You're already on the Free plan or can downgrade in Settings.");
      return;
    }
    try {
      await api.post("/billing/checkout", { plan_id: planId });
      toast.success(`Upgraded to ${planId} (mocked checkout)`);
      navigate("/app/home");
    } catch (e) {
      toast.error("Checkout failed");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:px-10">
          <Link to="/"><Logo /></Link>
          <Link to={user ? "/app/home" : "/login"}>
            <Button size="sm" variant="ghost" data-testid="pricing-login">
              {user ? "Dashboard" : "Log in"}
            </Button>
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-primary">PRICING</div>
          <h1 className="mt-4 font-display text-5xl font-bold tracking-tighter sm:text-6xl">
            Simple plans. Real value.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground">
            Start free, upgrade when your team starts winning back hours every week.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {plans.map((p) => (
            <div
              key={p.id}
              className={`relative flex flex-col rounded-2xl border bg-card p-6 ${
                p.popular ? "border-primary shadow-[0_20px_60px_-20px_rgba(194,65,12,0.3)]" : "border-border"
              }`}
              data-testid={`plan-card-${p.id}`}
            >
              {p.popular && (
                <div className="absolute -top-3 right-6 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                  Most popular
                </div>
              )}
              <div className="font-display text-2xl font-medium">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-5xl font-bold tracking-tighter">
                  {p.price_inr === 0 ? "₹0" : `₹${p.price_inr.toLocaleString("en-IN")}`}
                </span>
                <span className="text-sm text-muted-foreground">/month</span>
              </div>
              <div className="mt-2 font-mono text-xs text-muted-foreground">
                {p.hours >= 9999 ? "Unlimited hours" : `${p.hours} meeting hours / month`}
              </div>

              <ul className="mt-6 flex-1 space-y-3 text-sm">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleChoose(p.id)}
                variant={p.popular ? "default" : "outline"}
                className="mt-8 rounded-full"
                data-testid={`choose-plan-${p.id}`}
              >
                {p.id === "free" ? "Start free" : `Choose ${p.name}`}
              </Button>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-16 max-w-2xl rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
          All plans include AI summaries, action items, chat-with-meeting, and unlimited team members in
          your workspace. Built for India — pricing in INR, payment via Razorpay (UPI / Cards / NetBanking).
        </div>
      </section>
    </div>
  );
}
